import { Icon, P } from '@dnb/eufemia';
import { IconSize } from '@dnb/eufemia/components/icon';
import styled from '@emotion/styled';
import { getIconForType, type NavNodeType } from '@portals/shared-frontend/utils';

const TYPE_COLORS: Partial<Record<NavNodeType, string>> = {
  'api-doc': 'var(--color-summer-green)',
  'api-mgmt': 'var(--color-summer-green)',
  'api-ccr': 'var(--color-summer-green)',
  'mcp-doc': 'var(--color-violet)',
  'mcp-mgmt': 'var(--color-violet)',
  'event-doc': 'var(--color-sky-blue)',
  'event-mgmt': 'var(--color-sky-blue)',
};

interface Props {
  type: NavNodeType;
  size?: IconSize;
}
/**
 * TypeBadge Component
 *
 * A reusable badge component for displaying node types.
 * Shows a badge for API and Event types, and an icon for other types.
 */
export default function TypeBadge({ type, size = 'medium' }: Props) {
  const getBadgeLabel = () => {
    switch (type) {
      case 'api-doc': {
        return 'API (D)';
      }
      case 'api-mgmt': {
        return 'API (M)';
      }
      case 'api-ccr': {
        return 'API (CCR)';
      }
      case 'mcp-doc': {
        return 'MCP (D)';
      }
      case 'mcp-mgmt': {
        return 'MCP (M)';
      }
      case 'event-doc': {
        return 'Event (D)';
      }
      case 'event-mgmt': {
        return 'Event (M)';
      }
      default: {
        return 'Page';
      }
    }
  };

  // Show icon for types that don't use badges
  if (
    type !== 'api-doc' &&
    type !== 'api-mgmt' &&
    type !== 'api-ccr' &&
    type !== 'mcp-doc' &&
    type !== 'mcp-mgmt' &&
    type !== 'event-doc' &&
    type !== 'event-mgmt'
  ) {
    return (
      // Wrap in div to avoid layout shift when used alongside badges
      <div>
        <Icon icon={getIconForType(type)} size={size} />
      </div>
    );
  }

  return <StyledBadge $backgroundColor={TYPE_COLORS[type] ?? TYPE_COLORS.generic}>{getBadgeLabel()}</StyledBadge>;
}

const StyledBadge = styled(P)<{ $backgroundColor: string }>`
  height: 1.5rem;
  padding-inline: 0.4rem;
  border-radius: 0.5rem;
  color: white;
  font-size: 13px;
  font-weight: bold;
  white-space: nowrap;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
`;
